import type React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { MermaidDiagram } from '@/components/mermaid-diagram';
import { Card } from '@/components/ui/card';

interface MarkdownViewerProps {
  content: string;
  className?: string;
}

export const MarkdownViewer: React.FC<MarkdownViewerProps> = ({ content, className = '' }) => {
  return (
    <Card className={`p-6 ${className}`}>
      <div className="prose prose-slate dark:prose-invert max-w-none">
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            h1: ({ children, ...props }) => (
              <h1 className="text-3xl font-bold mb-4" {...props}>{children}</h1>
            ),
            h2: ({ children, ...props }) => (
              <h2 className="text-2xl font-semibold mb-3 mt-6" {...props}>{children}</h2>
            ),
            h3: ({ children, ...props }) => (
              <h3 className="text-xl font-semibold mb-2 mt-4" {...props}>{children}</h3>
            ),
            code: ({ children, className, ...props }) => {
              const match = /language-(\w+)/.exec(className ?? '');
              const lang = match ? match[1] : '';
              const isInline = !match;
              
              // Check if this is a mermaid code block
              if (lang === 'mermaid') {
                return (
                  <div className="my-4">
                    <MermaidDiagram chart={typeof children === 'string' ? children.replace(/\n$/, '') : ''} />
                  </div>
                );
              }
              
              if (isInline) {
                return (
                  <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-sm" {...props}>
                    {children}
                  </code>
                );
              }
              
              return (
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <code className={className} {...props}>
                      {children}
                    </code>
                  </pre>
                  {match && (
                    <div className="absolute top-2 right-2 text-xs text-gray-400">
                      {match[1]}
                    </div>
                  )}
                </div>
              );
            },
            pre: ({ children, ...props }) => {
              // Check if the pre contains a mermaid code block
              const codeElement = children as React.ReactElement<{ className?: string }>;
              if (codeElement?.props?.className === 'language-mermaid') {
                return <>{children}</>;
              }
              return <pre {...props}>{children}</pre>;
            },
            blockquote: ({ children, ...props }) => (
              <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic my-4" {...props}>
                {children}
              </blockquote>
            ),
            table: ({ children, ...props }) => (
              <div className="overflow-x-auto my-4">
                <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-600" {...props}>
                  {children}
                </table>
              </div>
            ),
            th: ({ children, ...props }) => (
              <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 bg-gray-100 dark:bg-gray-800 font-semibold" {...props}>
                {children}
              </th>
            ),
            td: ({ children, ...props }) => (
              <td className="border border-gray-300 dark:border-gray-600 px-4 py-2" {...props}>
                {children}
              </td>
            ),
            a: ({ children, href, ...props }) => (
              <a 
                href={href} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline"
                {...props}
              >
                {children}
              </a>
            ),
            ul: ({ children, ...props }) => (
              <ul className="list-disc list-inside my-2 space-y-1" {...props}>
                {children}
              </ul>
            ),
            ol: ({ children, ...props }) => (
              <ol className="list-decimal list-inside my-2 space-y-1" {...props}>
                {children}
              </ol>
            ),
            li: ({ children, ...props }) => (
              <li className="ml-4" {...props}>
                {children}
              </li>
            ),
            p: ({ children, ...props }) => (
              <p className="my-2 leading-relaxed" {...props}>
                {children}
              </p>
            ),
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    </Card>
  );
};